import { createFileRoute } from '@tanstack/react-router';
import PageGray from '../../components/General/PageGray';
import H1 from '../../components/Typography/Headings/H1';
import { useTranslation } from 'react-i18next';
import P from '../../components/Typography/P';
import { Search } from '@testbusters/search';
import Span from '../../components/Typography/Span';
import { useState, useEffect } from 'react';
import { cn } from '../../lib/utills';
import { useGetAttempts } from '../../hooks/attempts/useAttempts';
import InProgressAttempts from '../../components/SimulationsList/InProgress/InProgressAttempts';
import ToDoCollections from '../../components/SimulationsList/ToDo/ToDoCollections';
import Scrollbar from '../../components/SimulationsList/Scrollbar/Scrollbar';
import CompletedAttempts from '../../components/SimulationsList/CompletedAttempts/CompletedAttempts';
import { useSimulationsFilter } from '../../hooks/useSimulationsFilter';
import { useScrollbarSections } from '../../hooks/useScrollbarSections';
import GeneralError from '../../components/Errors/General';
import Loader from '../../components/Loader/Loader';
import { useGetCollections, useGetCollectionFilters } from '../../hooks/collections/useCollections';
import { CollectionType } from '@/types/collection';
import { useTestContext } from '../../context/TestContext';
import { toast } from 'sonner';
import ErrorNoCollections from '../../components/Errors/ErrorNoCollections';
import { Chip } from '@testbusters/chip';
import { Icon } from '@testbusters/icon';

export const Route = createFileRoute('/_sidebar/prove-ufficiali')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { selectedTestId, isFetching: isTestFetching } = useTestContext();
  const [officialTestsOnly, setOfficialTestsOnly] = useState(false);

  const {
    collections,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useGetCollections({
    testId: selectedTestId,
    type: CollectionType.SIMULATION,
    officialTests: true,
    sort: "name"
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (error) {
      toast.error(t('testSelector.loadError'));
    }
  }, [error]);

  const {
    attempts,
    isFetching: isAttemptsFetching,
    hasNextPage: attemptsHasNextPage,
    fetchNextPage: attemptsFetchNextPage,
    isFetchingNextPage: attemptsIsFetchingNextPage,
  } = useGetAttempts({
    testId: selectedTestId,
    officialTests: true
  });

  useEffect(() => {
    if (attemptsHasNextPage && !attemptsIsFetchingNextPage)
      attemptsFetchNextPage();
  }, [attemptsHasNextPage, attemptsIsFetchingNextPage]);

  const {
    handleSearch,
    search,
    isSearchActive,
    filteredCollections,
    inProgressAttempts,
    toDoCollections,
    completedAttempts,
    selectedTags,
    handleTagToggle,
    resetFilters,
  } = useSimulationsFilter({ collections, attempts, officialTestsOnly });

  useEffect(() => {
    resetFilters();
    setActiveSection(null);
    window.scrollTo({ top: 0 });
  }, [selectedTestId]);

  const { activeSection, setActiveSection } = useScrollbarSections({
    inProgressCount: inProgressAttempts.length,
    toDoCount: toDoCollections.length,
    completedCount: completedAttempts.length,
  });

  const { filters: availableTags } = useGetCollectionFilters({
    testId: selectedTestId,
    type: CollectionType.SIMULATION,
    officialTests: true,
    free: false,
  });

  const countFilteredResults =
    inProgressAttempts.length +
    toDoCollections.length +
    completedAttempts.length;

  useEffect(() => {
    const stored = sessionStorage.getItem('officialTestsOnly');
    if (stored !== null) {
      setOfficialTestsOnly(stored === 'true');
    }
  }, []);

  // const handleSwitchChange = (checked: boolean) => {
  //   setOfficialTestsOnly(checked);
  //   sessionStorage.setItem('officialTestsOnly', String(checked));
  // };

  if (
    isTestFetching ||
    (isFetching && collections.length === 0) ||
    (isAttemptsFetching && attempts.length === 0)
  )
    return <Loader fullScreen />;
  if (error) return <GeneralError />;

  return (
    <PageGray className="flex flex-col gap-6">
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-4">
          <H1>{t('nav.proveUfficiali')}</H1>
          <P>{t('simulationsList.subtitle')}</P>
        </div>
        <div className="flex flex-col-reverse lg:flex-row items-center gap-7">
          <Search
            onChange={(e) => handleSearch(e.target.value)}
            onClear={() => handleSearch('')}
            showClear
            placeholder={t('common.search')}
          />
          {/* <div className="flex gap-5 lg:w-fit w-full justify-between">
            <Span className="whitespace-nowrap font-semibold">
              {t('simulationsList.onlyOfficialTests')}
            </Span>
            <Switch
              checked={officialTestsOnly}
              onCheckedChange={handleSwitchChange}
            />
          </div> */}
        </div>
        {availableTags.length > 0 && (
          <div className="max-w-full">
            <div className="flex items-center gap-4 overflow-x-scroll no-scrollbar">
              {availableTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Chip
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={cn(
                      'cursor-pointer border rounded-[10px] select-none font-normal transition-colors',
                      isSelected
                        ? ' border-background-accent bg-background-accent-disabled font-semibold! shadow-2xl!'
                        : 'bg-white border-border-input-field',
                    )}
                  >
                    <Span className="capitalize text-inherit">
                      {tag}
                    </Span>
                    {isSelected && (
                      <Icon
                        name="close"
                        className="text-xl"
                      />
                    )}
                  </Chip>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {inProgressAttempts.length === 0 &&
      toDoCollections.length === 0 &&
      completedAttempts.length === 0 ? (
        <ErrorNoCollections />
      ) : (
        <div className="flex flex-col gap-7">
          {isSearchActive ? (
            <Span className="font-semibold">
              {countFilteredResults} {t('common.resultsFor')} "
              <Span className="font-bold">{search}</Span>"
            </Span>
          ) : (
            <Scrollbar
              activeSection={activeSection}
              onTabChange={setActiveSection}
              inProgressCount={inProgressAttempts.length}
              toDoCount={toDoCollections.length}
              completedCount={completedAttempts.length}
            />
          )}
          <div className={`flex flex-col gap-5`}>
            {(isSearchActive || activeSection === 'toDo') && (
              <div className="flex flex-col gap-5">
                {/* {!isSearchActive && toDoCollections.length > 0 && (
                  <Span className="font-semibold">
                    {t('simulationsList.toDoSimulations', {
                      count: toDoCollections.length,
                    })}
                  </Span>
                )} */}
                <ToDoCollections
                  collections={toDoCollections}
                  disablePagination={isSearchActive}
                />
              </div>
            )}
            {(isSearchActive || activeSection === 'inProgress') && (
              <div className="flex flex-col gap-5">
                {/* {!isSearchActive && inProgressAttempts.length > 0 && (
                  <Span className="font-semibold">
                    {t('simulationsList.inProgressSimulations', {
                      count: inProgressAttempts.length,
                    })}
                  </Span>
                )} */}
                {inProgressAttempts.length > 0 && (
                  <InProgressAttempts
                    collections={filteredCollections}
                    attempts={inProgressAttempts}
                    disablePagination={isSearchActive}
                  />
                )}
              </div>
            )}
            {(isSearchActive || activeSection === 'completed') && (
              <div className="flex flex-col gap-5">
                {/* {!isSearchActive && completedAttempts.length > 0 && (
                  <Span className="font-semibold">
                    {t('simulationsList.completedSimulations', {
                      count: completedAttempts.length,
                    })}
                  </Span>
                )} */}
                {completedAttempts.length > 0 && (
                  <CompletedAttempts
                    collections={filteredCollections}
                    attempts={completedAttempts}
                    disablePagination={isSearchActive}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </PageGray>
  );
}
